import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

/**
 * MobileFormModal - Modal de formulário bottom sheet para mobile
 * 
 * Props:
 * - isOpen: boolean - Controla se o modal está aberto
 * - onClose: function - Callback para fechar o modal
 * - title: string - Título do modal
 * - children: ReactNode - Conteúdo do modal (formulário)
 * - footer: ReactNode - Conteúdo do rodapé (botões de ação)
 * - className: string - Classes adicionais para o container de conteúdo
 * - stickyFooter: boolean - Se o footer deve ser fixo (default: false)
 */
export function MobileFormModal({ 
  isOpen, 
  onClose, 
  title, 
  children, 
  footer,
  className,
  stickyFooter = false
}) {
  // Prevent body scroll when modal is open
  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80"
            onClick={onClose}
          />
          
          {/* Bottom Sheet Modal */}
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed inset-x-0 bottom-0 z-50 bg-white dark:bg-slate-900 rounded-t-2xl max-h-[90vh] flex flex-col"
            data-testid="mobile-form-modal"
          >
            {/* Drag handle */}
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-12 h-1.5 bg-slate-300 dark:bg-slate-600 rounded-full" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-4 pb-3 border-b border-slate-200 dark:border-slate-700">
              <button
                type="button"
                onClick={onClose}
                className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                data-testid="mobile-modal-close"
              >
                <X className="w-5 h-5 text-slate-600 dark:text-slate-300" />
              </button>
              <h2 className="font-semibold text-lg text-slate-900 dark:text-white">
                {title}
              </h2>
              <div className="w-10" /> {/* Spacer for centering */}
            </div>

            {/* Scrollable Content */}
            <div 
              className={cn(
                "flex-1 overflow-y-auto p-4",
                className
              )}
            >
              {children}
              
              {/* Footer inside scroll area (not sticky) */}
              {footer && !stickyFooter && (
                <div className="pt-4 mt-4 border-t border-slate-200 dark:border-slate-700">
                  {footer}
                </div>
              )}
            </div>

            {/* Fixed Footer (sticky) */}
            {footer && stickyFooter && (
              <div className="border-t border-slate-200 dark:border-slate-700 p-4 bg-white dark:bg-slate-900 safe-area-bottom">
                {footer}
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

/**
 * MobileFormTabs - Tabs fullwidth para mobile
 */
export function MobileFormTabs({ tabs, activeTab, onTabChange }) {
  return (
    <div className="flex w-full border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 -mx-4 px-4">
      {tabs.map((tab) => (
        <button
          key={tab.value}
          type="button"
          onClick={() => onTabChange(tab.value)}
          className={cn(
            "flex-1 py-3 text-sm font-medium transition-colors relative",
            activeTab === tab.value
              ? "text-orange-500"
              : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
          )}
          data-testid={`mobile-tab-${tab.value}`}
        >
          <div className="flex items-center justify-center gap-2">
            {tab.icon && <tab.icon className="w-4 h-4" />}
            <span>{tab.label}</span>
          </div>
          {activeTab === tab.value && (
            <motion.div
              layoutId="activeTab"
              className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-500"
            />
          )}
        </button>
      ))}
    </div>
  );
}

/**
 * MobileFormInput - Input estilizado para modal mobile (sem bordas, apenas linha inferior)
 */
export function MobileFormInput({ 
  label, 
  placeholder, 
  value, 
  onChange, 
  type = 'text',
  className,
  ...props 
}) {
  return (
    <div className={cn("py-2", className)}>
      {label && (
        <label className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">
          {label}
        </label>
      )}
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full bg-transparent border-0 border-b border-slate-200 dark:border-slate-700 focus:border-orange-500 dark:focus:border-orange-500 focus:ring-0 px-0 py-3 text-base placeholder:text-slate-400 dark:placeholder:text-slate-500 text-slate-900 dark:text-white outline-none"
        {...props}
      />
    </div>
  );
}

/**
 * MobileFormTextarea - Textarea estilizado para modal mobile
 */
export function MobileFormTextarea({ 
  label, 
  placeholder, 
  value, 
  onChange, 
  rows = 3,
  className,
  ...props 
}) {
  return (
    <div className={cn("py-2", className)}>
      {label && (
        <label className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">
          {label}
        </label>
      )}
      <textarea
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        rows={rows}
        className="w-full bg-transparent border-0 border-b border-slate-200 dark:border-slate-700 focus:border-orange-500 dark:focus:border-orange-500 focus:ring-0 px-0 py-3 text-base placeholder:text-slate-400 dark:placeholder:text-slate-500 text-slate-900 dark:text-white resize-none outline-none"
        {...props}
      />
    </div>
  );
}

/**
 * MobileFormFooter - Footer padrão com botões empilhados (principal em cima)
 */
export function MobileFormFooter({ 
  onCancel, 
  onSubmit, 
  cancelText = 'Cancelar', 
  submitText = 'Salvar',
  isSubmitting = false,
  submitDisabled = false
}) {
  return (
    <div className="flex flex-col gap-3">
      <Button
        type="button"
        className="w-full rounded-2xl bg-orange-500 hover:bg-orange-600 h-14 text-base font-semibold"
        onClick={onSubmit}
        disabled={isSubmitting || submitDisabled}
      >
        {submitText}
      </Button>
      <Button
        type="button"
        variant="outline"
        className="w-full rounded-2xl h-14 text-base font-semibold"
        onClick={onCancel}
        disabled={isSubmitting}
      >
        {cancelText}
      </Button>
    </div>
  );
}

export default MobileFormModal;
